import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

/*-------------------------------------------------------------------------------
  Initialize
--------------------------------------------------------------------------------*/
export default class Smoother {
  constructor(obj,onResizeCallback) {
    this.obj = obj;
    this.setting  = new Setting(this.obj);
    this.isMobile = this.setting.isMobile;
    this.onResizeCallback = onResizeCallback;

    this.bar       = this.obj.bar ? this.obj.bar : true;
    this.scrollbar = null;
    this.cursor    = this.obj.cursor ? this.obj.cursor : true;
    this.cursors   = null;

    this.scroller = window;
    this.smoothScroll = null;
    if (!this.isMobile) {
      this.scroller = this.setting.smoothView;
      this.smoothScroll = new SmoothScroll(this.setting);
      this.anchor = new AnchorLink(this.setting, this.smoothScroll);
      this.addOption();
    }

    window.addEventListener("resize", this.resize.bind(this));
    this.handleResize();
  }

  addOption() {
    if(this.bar) {
      this.scrollbar = new ScrollBar();
    }
    if(this.cursor) {
      this.cursors = new CustomCursor;
    }
  }

  removeOption() {
    this.scrollbar?.remove();
    this.cursors?.remove();
  }

  resize() {
    this.setting.resize();
    this.isMobile = this.setting.isMobile;
    if (this.smoothScroll) {
      this.smoothScroll.restart(this.isMobile);
    } else if (!this.isMobile) {
      this.smoothScroll = new SmoothScroll(this.setting);
      this.addOption();
    } else if (this.isMobile) {
      this.removeOption();
    }
    this.handleResize();
  }

  handleResize() {
    if (typeof this.onResizeCallback === "function") {
      this.onResizeCallback(this.isMobile);
    }
  }
}


/*-------------------------------------------------------------------------------
  ScrollBar
--------------------------------------------------------------------------------*/
class ScrollBar {
  constructor() {
    this.handle = document.createElement("div");
    this.handle.className = "custom-scrollbar-handle";
    document.body.appendChild(this.handle);
    this.init();
  }

  init() {
    this.handle.addEventListener("mousedown", this.handleMouseDown.bind(this));
    ScrollTrigger.create({
      trigger: document.body,
      start: "top top",
      end: "bottom bottom",
      onUpdate: (self) => this.updateHandle(self.progress, document.body.scrollHeight)
    });
  }

  remove(){
    if( this.handle ) {
      this.handle.remove();
    }
  }

  updateHandle(progress, contentHeight) {
    const handleHeight = (window.innerHeight / contentHeight) * window.innerHeight;
    this.handle.style.height = `${handleHeight}px`;
    this.handle.style.top = `${progress * (window.innerHeight - handleHeight)}px`;
  }

  handleMouseDown(e) {
    e.preventDefault();
    const startY = e.clientY;
    const startTop = this.handle.offsetTop;
    const maxHeight = window.innerHeight - this.handle.offsetHeight;

    const onMouseMove = (e) => {
      const deltaY = e.clientY - startY;
      const newTop = Math.min(Math.max(startTop + deltaY, 0), maxHeight);
      const progress = newTop / maxHeight;
      window.scrollTo(0, progress * (document.body.scrollHeight - window.innerHeight));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }
}

/*-------------------------------------------------------------------------------
  AnchorLink
--------------------------------------------------------------------------------*/
class AnchorLink {
  constructor({ url, header, anchorStart, anchorDelay },smoothScroll) {
    this.url          = url;
    this.header       = header;
    this.anchorStart  = anchorStart;
    this.anchorDelay  = anchorDelay;
    this.smoothScroll = smoothScroll;
    this.smoother     = smoothScroll.smoother;
    this.init();
  }

  init() {
    this.clickAnchorScroll();
    this.hasAnchorUrl();
    this.smoother.update();
  }

  AnchorScroll(elem) {
    if (!elem) return;
    gsap.to(window, {
      ease: "power1.inOut",
      scrollTo: { y: elem, offsetY: this.header }
    });
  }

  clickAnchorScroll() {
    document.querySelectorAll('a[href*="#"]').forEach((anchor) => {
      anchor.addEventListener("click", (e) => {
        const hash = anchor.hash;
        if (hash === "#") return;
        const elem = document.getElementById(hash.replace("#", ""));
        const currentUrl = window.location.href.split("#")[0];
        const linkUrl = anchor.href.split("#")[0];

        if (!elem) return;

        if (currentUrl === linkUrl) {
          this.AnchorScroll(elem);
          e.preventDefault();
        } else {
          window.location.href = anchor.href;
        }
      });
    });
  }

  hasAnchorUrl() {
    if (this.anchorStart && this.url.includes("#")) return;
    const hash = this.url.split("#").pop();
    const elem = document.getElementById(hash);
    if (elem) {
      gsap.set(window, {
        ease: 'none',
        scrollTo: { y: elem },
      });
      const timeline = gsap.timeline({});
      timeline.set(window, {
        ease: 'none',
        scrollTo: { y: elem, offsetY: this.header , autoKill: true },
      });
    }
  }
}

/*-------------------------------------------------------------------------------
  SmoothScroll
--------------------------------------------------------------------------------*/
class SmoothScroll {
  constructor({ smoothView, smoothArea, setHeight, windowSize, scrub, isMobile }) {
    this.smoothView = smoothView;
    this.smoothArea = smoothArea;
    this.setHeight  = setHeight;
    this.windowSize = windowSize;
    this.scrub      = scrub;
    this.isMobile   = isMobile;
    this.scrollY    = window.scrollY || document.documentElement.scrollTop;

    this.smoother = null;
    this.animation = null;

    this.scrollStart = !this.isMobile;
    if (!this.isMobile) this.init();
  }

  init() {
    const getProp = gsap.getProperty(this.smoothView);
    const setProp = gsap.quickSetter(this.smoothView, "y", "px");
    const setScroll = ScrollTrigger.getScrollFunc(window);
    let isProxyScrolling;

    ScrollTrigger.defaults({ scroller: this.smoothView });
    ScrollTrigger.scrollerProxy(this.smoothView, {
      scrollTop(value) {
        if (arguments.length) {
          setProp(-value);
          setScroll(value);
          return;
        }
        return -getProp("y");
      },
      scrollHeight: () => document.body.scrollHeight,
      getBoundingClientRect: () => ({ top: 0, left: 0, width: this.windowSize.w, height: this.windowSize.h })
    });

    this.animation = gsap.to(this.smoothView, {
      y: () => document.documentElement.clientHeight - this.setHeight(),
      ease: "none",
      onUpdate: ScrollTrigger.update
    });

    this.smoother = ScrollTrigger.create({
      animation: this.animation,
      scroller: window,
      invalidateOnRefresh: true,
      start: "top top",
      end: "bottom bottom",
      refreshPriority: -999,
      scrub: this.scrub,
      onUpdate: (self) => {
        if (isProxyScrolling) {
          this.killScrub(self);
          isProxyScrolling = false;
        }
        this.scrollY = window.scrollY || document.documentElement.scrollTop;
      },
      onRefresh: this.killScrub
    });
  }

  killScrub(trigger) {
    const scrub = trigger.getTween ? trigger.getTween() : gsap.getTweensOf(trigger.animation)[0];
    if (scrub) scrub.pause();
    trigger.animation.progress(trigger.progress);
  }

  restart(isMobile) {
    this.isMobile = isMobile;
    if (this.scrollStart && this.smoother !== null) {
      this.smoother.refresh();
    }

    if (this.scrollStart && this.isMobile) {
      this.scrollStart = false;
      gsap.set(this.smoothArea, { position: "relative", transform: "unset" });
      this.smoother.disable();
    } else if (!this.scrollStart && !this.isMobile && this.smoother !== null) {
      this.scrollStart = true;
      gsap.set(this.smoothArea, { position: "fixed", transform: "0px 0px" });
      this.smoother.enable();
    } else if (!this.scrollStart && !this.isMobile && this.smoother === null) {
      this.scrollStart = true;
      this.init();
    }
  }
}

/*-------------------------------------------------------------------------------
  Setting
--------------------------------------------------------------------------------*/
class Setting {
  constructor(obj) {
    this.obj = obj;

    this.windowSize = {
      w: document.documentElement.clientWidth || window.innerWidth,
      h: document.documentElement.clientHeight || window.innerHeight
    };
    this.height = this.windowSize.h;

    this.breakpoints = this.obj.breakpoints || 768;
    this.scrub       = this.obj.scrub || 0.8;
    this.headerId    = this.obj.headerId || null;
    this.header      = null;
    this.url         = this.obj.url || location.href;
    this.anchorStart = this.obj.anchor?.bool || false;
    this.anchorDelay = this.obj.anchor?.delay || 0;

    this.isMobile = this.windowSize.w < this.breakpoints;

    this.smoothView = null;
    this.smoothArea = null;

    this.init();
  }

  init() {
    const create = new CreateArea(this.obj);
    this.smoothView = create.scrollElem.smoothView;
    this.smoothArea = create.scrollElem.smoothArea;
    this.setHeight();
    this.setObserver();
    this.setHeader();
    if (!this.isMobile) {
      this.setWrap();
    }
  }

  setHeight() {
    this.height = this.smoothView.clientHeight;
    document.body.style.height = `${this.height}px`;
    return this.height;
  }

  setHeader() {
    if (!this.headerId) return;
    this.header = document.getElementById(this.headerId)?.clientHeight || 0;
  }

  setWrap() {
    gsap.set(this.smoothArea, { overflow: "hidden", position: "fixed", height: "100%", width: "100%", top: 0, left: 0, right: 0, bottom: 0 });
    gsap.set(this.smoothView, { overflow: "visible", width: "100%" });
  }

  setObserver() {
    const resizeObserver = new ResizeObserver(() => {
      this.setHeight();
      ScrollTrigger.refresh();
    });
    resizeObserver.observe(this.smoothView);
  }

  removeWrap() {
    gsap.set(this.smoothView, { position: "relative", transform: "unset" });
  }

  resetWrap() {
    gsap.set(this.smoothView, { position: "fixed", transform: "0px 0px" });
  }

  resize() {
    this.windowSize = {
      w: document.documentElement.clientWidth || window.innerWidth,
      h: document.documentElement.clientHeight || window.innerHeight
    };
    this.isMobile = this.windowSize.w < this.breakpoints;
    if (this.isMobile) {
      this.removeWrap();
    } else {
      this.setWrap();
      this.resetWrap();
    }
  }
}

/*-------------------------------------------------------------------------------
  CreateArea
--------------------------------------------------------------------------------*/
class CreateArea {
  constructor(obj) {
    this.obj = obj;
    this.scrollElem = {};
    this.init();
  }

  init() {
    const viewport = document.createElement('div');
    viewport.id = this.obj.smoothView || 'smooth_view';
    const container = document.createElement('div');
    container.id = this.obj.smoothArea || 'scroll_wrapper';
    const wrapper = document.querySelector(this.obj.target);
    const wrapperNodes = Array.from(wrapper.childNodes);
    wrapper.insertBefore(container, wrapper.firstChild);
    wrapperNodes.forEach(child => viewport.appendChild(child));
    container.appendChild(viewport);

    this.scrollElem = {
      smoothView: viewport,
      smoothArea: container
    };
  }
}

/*-------------------------------------------------------------------------------
  Cursor
  Copyright (c) 2024 by Arseny M. (https://codepen.io/theseventh/pen/LYVqMYb)
--------------------------------------------------------------------------------*/

export class CustomCursor {
  constructor() {
    this.cursor = document.createElement('div');
    this.cursor.className = 'cursor';

    this.cursorF = document.createElement('div');
    this.cursorF.className = 'cursor-flame';

    this.cursorX = 0;
    this.cursorY = 0;
    this.pageX = 0;
    this.pageY = 0;
    this.size = 8;
    this.sizeF = 36;
    this.followSpeed = 0.16;

    document.body.appendChild(this.cursor);
    document.body.appendChild(this.cursorF);

    if ('ontouchstart' in window) {
      this.cursor.style.display = 'none';
      this.cursorF.style.display = 'none';
    }

    this.cursor.style.setProperty('--size', `${this.size}px`);
    this.cursorF.style.setProperty('--size', `${this.sizeF}px`);

    this.startY = null;
    this.endY = null;
    this.clicked = false;

    window.addEventListener('mousemove', (e) => this.onMouseMove(e));
    window.addEventListener('mousedown', (e) => this.onMouseDown(e), false);
    window.addEventListener('mouseup', (e) => this.onMouseUp(e), false);
    window.addEventListener('touchstart', (e) => this.onMouseDown(e), false);
    window.addEventListener('touchmove', (e) => this.onTouchMove(e), false);
    window.addEventListener('touchend', (e) => this.onMouseUp(e), false);

    this.addLinkHoverListeners();

    this.loop();
  }

  remove () {
    if( this.cursor ) {
      this.cursor.remove();
    }
    if( this.cursorF ) {
      this.cursorF.remove();
    }
  }

  onMouseMove(e) {
    this.pageX = e.clientX;
    this.pageY = e.clientY;
    this.cursor.style.left = `${e.clientX - this.size / 2}px`;
    this.cursor.style.top = `${e.clientY - this.size / 2}px`;
  }

  loop() {
    this.cursorX = this.lerp(this.cursorX, this.pageX, this.followSpeed);
    this.cursorY = this.lerp(this.cursorY, this.pageY, this.followSpeed);
    this.cursorF.style.top = `${this.cursorY - this.sizeF / 2}px`;
    this.cursorF.style.left = `${this.cursorX - this.sizeF / 2}px`;
    requestAnimationFrame(() => this.loop());
  }

  lerp(a, b, n) {
    return (1 - n) * a + n * b;
  }

  onMouseDown(e) {
    gsap.to(this.cursor, {scale: 4.5});
    gsap.to(this.cursorF, {scale: 0.4});

    this.clicked = true;
    this.startY = e.clientY || e.touches[0].clientY || e.targetTouches[0].clientY;
  }

  onMouseUp(e) {
    gsap.to(this.cursor, {scale: 1});
    gsap.to(this.cursorF, {scale: 1});

    this.endY = e.clientY || this.endY;
    if (this.clicked && this.startY && Math.abs(this.startY - this.endY) >= 40) {
      this.clicked = false;
      this.startY = null;
      this.endY = null;
    }
  }

  onTouchMove(e) {
    if (this.clicked) {
      this.endY = e.touches[0].clientY || e.targetTouches[0].clientY;
    }
  }

  addLinkHoverListeners() {
    const links = document.querySelectorAll('a');
    links.forEach(link => {
      link.addEventListener('mouseenter', () => this.onLinkHover(true));
      link.addEventListener('mouseleave', () => this.onLinkHover(false));
    });
  }

  onLinkHover(isHover) {
    if (isHover) {
      this.cursorF.classList.add('is-hover');
    } else {
      this.cursorF.classList.remove('is-hover');
    }
  }

}
